/**
 * CLUB-780 — sends Energy members straight to the dashboard instead of the
 * "Energy Plan added" interstitial.
 *
 * Named middleware on the completion route itself rather than a change at the
 * three `router.push` call sites that lead here (the main energy funnel at
 * `pages/dashboard/vault/[...product]/form/energy/step/[step].vue`, the
 * smart-bill confirm at `.../form/product/preview.vue`, and the bill-parse
 * poller in `components/organisms/UploaderWidget/index.vue`). One rule, one
 * representation — and this also covers direct URLs, browser history, and
 * whatever caller is added next. Every other vertical still renders the page.
 *
 * The dashboard's Review Status modal takes over both of the interstitial's
 * jobs. It reliably fires for the vault just added because the backend selects
 * vaults created since the member's last dismissal and gates that on a
 * confidence score which `VaultController::store()` writes synchronously inside
 * `POST /vaults` — not in a queued job. Its Quick Compare CTA is built from the
 * same redirect branch this page used and applies the same Capacitor
 * `openInBrowser` hand-off. See
 * agent-os/specs/2026-09-08-0855-club-780-product-vault-updates/shape.md.
 */
export default defineNuxtRouteMiddleware(to => {
  if (resolveVertical(to.params.vertical) !== 'energy') return

  /*
   * The redirect itself runs in both environments, so a direct load of this URL
   * on the SSR build never renders the interstitial before bouncing. Only the
   * side effects are client-gated: the store reset is pointless on a render
   * that is about to be discarded, and $tracking comes from a .client plugin.
   */
  if (import.meta.client) {
    /*
     * The interstitial's two CTAs were the only callers of resetVaultFormData
     * on the energy path — the submit handler clears only resetInlineEdit and
     * leadAddress — so without this, stale form data leaks into the next
     * add-product flow in the same session.
     */
    try {
      useVaultStore().resetVaultFormData()
    } catch {
      // Landing on the dashboard matters more than the reset.
    }

    /*
     * Replaces the conversion signal the interstitial provided implicitly: a
     * pageview fires on every route change (app.vue), and that was the only
     * event emitted for every energy add. `Vault Energy Lead Updated` is gated
     * on a lead being returned, so it cannot stand in.
     *
     * Needs registering in the GTM container to reach GA4 — see the spec.
     */
    try {
      const { $tracking } = useNuxtApp()
      $tracking.customEvent('Vault Product Added', {
        vertical: 'energy',
        completion_source: resolveQueryValue(to.query.completion_source)
      })
    } catch {
      // Analytics must never block navigation.
    }
  }

  // `replace` so browser back cannot resurface the page we just skipped.
  return navigateTo('/dashboard/', { replace: true })
})

/**
 * Mirrors the page's own vertical resolution — `[...product]` is a catch-all, so
 * the sibling `[vertical]` param can arrive as an array.
 */
function resolveVertical (param: unknown): string {
  const value = Array.isArray(param) ? param[0] : param

  return typeof value === 'string' ? value.toLowerCase() : ''
}

/**
 * A repeated query key (`?completion_source=a&completion_source=b`) arrives as
 * an array, so the value has to be narrowed rather than cast — an array pushed
 * into the dataLayer would break any GTM variable reading this field. Takes the
 * first entry, and returns null for anything not a non-empty string.
 */
function resolveQueryValue (param: unknown): string | null {
  const value = Array.isArray(param) ? param[0] : param

  return typeof value === 'string' && value !== '' ? value : null
}
